import {
  BackButtonCWUI,
  CustomerCategoryTitleUI,
  ButtonUI,
} from '@next-order/next-order-components';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AnimationWrapper from 'components/AnimationWrapper';
import LottieAnimation from 'components/Lottie';
import {
  LocationMarkerIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { getSavedAddressesAPI, deleteAddressAPI } from './actions';
import AddressEdit from './AddressEdit';
import { PlusRoundedThickIcon } from 'nexticons/outline';
import { updateSavedAddresses } from 'containers/Home/actions';

export default function SavedAddresses({ userDetails, onBack }) {
  const storeConfig = useSelector(selectStoreConfig);
  const savedAddresses = useSelector(
    (state) => state.homeReducer?.savedAddresses || []
  );
  const [error, setError] = useState('');
  const [showAddressEdit, setShowAddressEdit] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deletingAddressId, setDeletingAddressId] = useState(null);

  const dispatch = useDispatch();

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setShowAddressEdit(true);
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setShowAddressEdit(true);
  };

  const handleAddressEditBack = () => {
    setShowAddressEdit(false);
    setEditingAddress(null);
  };

  const fetchAddresses = async () => {
    if (userDetails?._id && storeConfig.cloudFunctionsUrl) {
      try {
        const response = await getSavedAddressesAPI({
          userId: userDetails._id,
          cloudFunctionsUrl: storeConfig.cloudFunctionsUrl,
        });
        const savedAddresses = response?.data?.addresses;
        if (savedAddresses) {
          dispatch(updateSavedAddresses(savedAddresses));
        }
      } catch (err) {
        console.error('Error refreshing addresses:', err);
      }
    }
  };

  const handleAddressSave = () => {
    fetchAddresses();
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      setDeletingAddressId(addressId);

      await deleteAddressAPI({
        userId: userDetails._id,
        addressId,
        cloudFunctionsUrl: storeConfig.cloudFunctionsUrl,
      });

      // Refresh the addresses list after successful delete
      try {
        fetchAddresses();
      } catch (refreshError) {
        console.error('Error refreshing addresses after delete:', refreshError);
        // Fallback: Remove the deleted address from the list locally using Redux
        const updatedAddresses = savedAddresses.filter(
          (addr) => addr.id !== addressId
        );
        dispatch(updateSavedAddresses(updatedAddresses));
      }
    } catch (err) {
      console.error('Error deleting address:', err);
      setError(err.message || 'Failed to delete address. Please try again.');
    } finally {
      setDeletingAddressId(null);
    }
  };

  const { addresses, isLoading } = savedAddresses || {};

  const renderAddressItem = (address) => {
    return (
      <div
        key={address.id}
        className='bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm'
      >
        <div className='flex items-center gap-4'>
          {/* Map Icon */}
          <div className='flex-shrink-0'>
            <div className='w-10 h-10 hover:bg-gray-100 rounded-full flex items-center justify-center'>
              <LocationMarkerIcon className='w-5 h-5 text-gray-600' />
            </div>
          </div>

          {/* Address Details */}
          <div className='flex-1 min-w-0 flex flex-col gap-2'>
            <div className='flex items-center gap-2 mb-1'>
              <div className='text-base font-semibold text-gray-900'>
                {address.label}
              </div>
              {address.isDefault && (
                <span className='px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium'>
                  Default
                </span>
              )}
            </div>
            <div className='text-sm text-gray-700 font-medium'>
              {address.addressLine1}
            </div>
            <div className='text-sm text-gray-500'>{address.addressLine2}</div>
          </div>

          {/* Action Buttons */}
          <div className='flex-shrink-0 flex items-center gap-1'>
            {/* Edit Button */}
            <button
              onClick={() => handleEditAddress(address)}
              className='w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[var(--main-theme-color)] rounded-full transition-colors duration-200 relative overflow-hidden'
            >
              <div
                className='absolute inset-0 rounded-full'
                style={{
                  backgroundColor: 'var(--main-theme-color)',
                  opacity: 0.1,
                }}
              />
              <PencilIcon className='w-4 h-4 relative z-10' />
            </button>

            {/* Delete Button */}
            <button
              onClick={() => handleDeleteAddress(address.id)}
              disabled={deletingAddressId === address.id}
              className='w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[var(--main-theme-color)] rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden'
            >
              <div
                className='absolute inset-0 rounded-full'
                style={{
                  backgroundColor: 'var(--main-theme-color)',
                  opacity: 0.1,
                }}
              />
              {deletingAddressId === address.id ? (
                <div className='w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin relative z-10' />
              ) : (
                <TrashIcon className='w-4 h-4 relative z-10' />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // If showing address edit, render AddressEdit component
  if (showAddressEdit) {
    return (
      <AddressEdit
        address={editingAddress}
        userDetails={userDetails}
        onBack={handleAddressEditBack}
        onSave={handleAddressSave}
      />
    );
  }

  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
      className='profile-section'
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <div className='relative h-screen pt-4'>
              <BackButtonCWUI onClick={onBack} extendedClassName='mx-5' />

              <CustomerCategoryTitleUI extendedClassName='mt-3.25 !mb-0 ml-[-1px] px-5'>
                Saved Addresses
              </CustomerCategoryTitleUI>

              {/* Add Address Button - Positioned like Print Receipt in OrderView */}
              <div className='absolute top-5 right-5'>
                <ButtonUI
                  extendedClassName='!border-0 shadow-modifier-badge'
                  fontSize='sm'
                  rounded='full'
                  variant='outlined'
                  onClick={handleAddAddress}
                  leftIcon={<PlusRoundedThickIcon strokeWidth={3} width={18} />}
                >
                  Add Address
                </ButtonUI>
              </div>

              {/* Scrollable Addresses Container */}
              <div className='overflow-y-auto pb-20 h-[calc(100vh-200px)] md:h-[calc(100vh-150px)] mt-6 px-5'>
                {isLoading ? (
                  <div className='flex justify-center items-center h-[60vh] text-xs'>
                    <LottieAnimation statusText='' />
                  </div>
                ) : error ? (
                  <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-red-700'>
                    {error}
                  </div>
                ) : addresses.length === 0 ? (
                  <div className='text-center py-12'>
                    <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                      <LocationMarkerIcon className='w-8 h-8 text-gray-400' />
                    </div>
                    <div className='text-gray-500 text-lg mb-2'>
                      No saved addresses
                    </div>
                    <div className='text-gray-400 text-sm mb-6'>
                      Add your first address to make ordering faster and easier.
                    </div>
                    <ButtonUI
                      extendedClassName='!border-2 border-gray-300 hover:bg-gray-50'
                      fontSize='sm'
                      rounded='lg'
                      variant='outlined'
                      onClick={handleAddAddress}
                      leftIcon={<PlusIcon className='w-4 h-4' />}
                    >
                      Add Your First Address
                    </ButtonUI>
                  </div>
                ) : (
                  <div>
                    <div className='text-sm text-gray-600 mb-4'>
                      {addresses.length} Saved Address
                      {addresses.length !== 1 ? 'es' : ''}
                    </div>
                    {addresses.map(renderAddressItem)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </AnimationWrapper>
  );
}
